"use client";

import React from "react";

interface UseDebounceReturn<T> {
  debouncedValue: T;
  cancel: () => void;
  isPending: () => boolean;
}

export function useDebounce<T>(
  value: T,
  delay: number = 500,
): UseDebounceReturn<T> {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);
  const [pending, setPending] = React.useState<boolean>(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      setPending(false);
    }
  }, []);

  React.useEffect(() => {
    cancel();
    setPending(true);

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
      setPending(false);
      timeoutRef.current = null;
    }, delay);

    return cancel;
  }, [value, delay, cancel]);

  const isPending = React.useCallback(() => pending, [pending]);

  return { debouncedValue, cancel, isPending };
}
